import type { IStringifyOptions } from 'qs';
import type { GlobalFilterItem } from '@weberus/shared-types';
import type { GlobalFilter, QuickFilterWithGlobalFlag } from '@/app/_shared/store/search-filter';
import { useSetAtom, useAtomValue } from 'jotai';
import { parse, stringify } from 'qs';
import { isNil, omitBy } from 'lodash-es';
import { globalFilterAtom, globalFilterParamsAtom, searchTypeKeyAtom } from '@/app/_shared/store/search-filter';
import { globalFilterTypeCheckers } from '../filter-renderer';
import { updateFilterStorage } from '../../plp-srp-filter/filter-storage';

const quickFilterQueryStr = (
  key: string,
  code: string | undefined,
  isSelected: boolean,
  globalFilter: GlobalFilterItem[] | undefined,
) => {
  // filter 는 ICodeTypeDetailFilterItem 타입과 대응
  const filterObj: Record<string, string[]> = {};
  // switchFilter 는 IBoolTypeDetailFilterItem 타입과 대응
  const switchFilterObj: Record<string, boolean> = {};
  // rangeFilter 는 INumberRangeTypeDetailFilterItem 타입과 대응
  const rangeFilterObj: Record<string, [number, number]> = {};

  globalFilter?.forEach((item) => {
    if (globalFilterTypeCheckers.isSwitchType(item)) {
      const { selected, key: _key } = item.items?.[0] || {};
      if (selected) {
        switchFilterObj[_key] = true;
      }
    } else if (globalFilterTypeCheckers.isChipType(item) || globalFilterTypeCheckers.isCheckboxListType(item)) {
      item.items
        .filter(({ selected }) => selected)
        .forEach((subItem) => {
          if (!filterObj[subItem.key]) {
            filterObj[subItem.key] = [];
          }
          filterObj[subItem.key].push(subItem.code);
        });
    } else if (globalFilterTypeCheckers.isAccordionType(item)) {
      item.items.forEach((subItem) => {
        subItem.items
          .filter(({ selected }) => selected)
          .forEach((chip) => {
            if (!filterObj[chip.key]) {
              filterObj[chip.key] = [];
            }
            filterObj[chip.key].push(chip.code);
          });
      });
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeType(item)) {
      const { selected, key: _key } = item.items?.[0] || {};
      if (selected) {
        const value = item.items?.[0].value;
        if (value) {
          rangeFilterObj[_key] = [value.from, value.to];
        }
      }
    }
  });

  if (code) {
    // code가 있는 경우 ICodeTypeDetailFilterItem 타입과 대응
    if (isSelected) {
      if (!filterObj[key]) {
        filterObj[key] = [];
      }
      filterObj[key].push(code);
    } else {
      filterObj[key] = filterObj[key]?.filter((item) => item !== code);
    }
  } else if (isSelected) {
    // code가 없는 경우 IBoolTypeDetailFilterItem 타입과 대응
    switchFilterObj[key] = true;
  } else {
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete switchFilterObj[key];
  }

  const stringifyOptions: IStringifyOptions = {
    arrayFormat: 'comma',
    skipNulls: true,
    filter: (_, val) => (val ? val : undefined), // 빈 문자열('') 제거
  };

  const filterQuery = stringify(filterObj, stringifyOptions) || undefined;
  const switchFilterQuery = stringify(switchFilterObj, stringifyOptions) || undefined;
  const rangeFilterQuery = stringify(rangeFilterObj, stringifyOptions) || undefined;

  return {
    filterQuery,
    switchFilterQuery,
    rangeFilterQuery,
  };
};

const getTempQuickFilterParams = (
  key: string,
  code: string | undefined,
  isSelected: boolean,
  globalFilter: GlobalFilterItem[] | undefined,
): GlobalFilter[] | undefined => {
  let filterArray: GlobalFilter[] = [];
  let switchFilterArray: GlobalFilter[] = [];
  const rangeFilterArray: GlobalFilter[] = [];

  // globalFilterItems의 초기 상태를 기반으로 배열 초기화
  globalFilter?.forEach((item) => {
    if (globalFilterTypeCheckers.isSwitchType(item)) {
      const { selected, key: _key } = item.items?.[0] || {};
      if (selected) {
        switchFilterArray.push({ key: _key });
      }
    } else if (globalFilterTypeCheckers.isChipType(item) || globalFilterTypeCheckers.isCheckboxListType(item)) {
      item.items
        .filter(({ selected }) => selected)
        .forEach((subItem) => filterArray.push({ key: subItem.key, code: subItem.code }));
    } else if (globalFilterTypeCheckers.isAccordionType(item)) {
      item.items.forEach((subItem) => {
        subItem.items
          .filter(({ selected }) => selected)
          .forEach((chip) => filterArray.push({ key: chip.key, code: chip.code }));
      });
    } else if (globalFilterTypeCheckers.isHistogramNumberRangeType(item)) {
      const { selected, key: _key, value } = item.items?.[0] || {};
      if (selected && value) {
        rangeFilterArray.push({ key: _key, value: { from: value.from, to: value.to } });
      }
    }
  });

  // 현재 선택한 필터의 추가/제거 처리
  if (code) {
    // ICodeTypeDetailFilterItem 타입 대응
    if (isSelected) {
      filterArray.push({ key, code });
    } else {
      filterArray = filterArray.filter((i) => !(i.key === key && i.code === code));
    }
  } else if (isSelected) {
    // IBoolTypeDetailFilterItem 타입 대응
    switchFilterArray.push({ key });
  } else {
    switchFilterArray = switchFilterArray.filter((i) => i.key !== key);
  }

  return [...filterArray, ...switchFilterArray, ...rangeFilterArray];
};

export const useSyncQuickFilterWithQuery = (sessionKey: string) => {
  // store getter
  const globalFilter = useAtomValue(globalFilterAtom);
  const searchTypeKey = useAtomValue(searchTypeKeyAtom);

  // store setter
  const setGlobalFilterParam = useSetAtom(globalFilterParamsAtom);

  const syncQuickFilterWithQuery = (item: QuickFilterWithGlobalFlag['items'][0]) => {
    const currentQuery = parse(new URLSearchParams(window.location.search).toString());
    const queryStr = quickFilterQueryStr(item.key, item.code, !item.selected, globalFilter?.items);

    const newQuery = omitBy(
      {
        ...currentQuery,
        filter: queryStr.filterQuery,
        switchFilter: queryStr.switchFilterQuery,
        rangeFilter: queryStr.rangeFilterQuery,
      },
      (value) => isNil(value) || value === '',
    );

    const updatedUrl = `?${stringify(newQuery)}`;
    window.history.replaceState(null, '', updatedUrl);

    // 글로벌 필터 파라미터 설정
    setGlobalFilterParam(getTempQuickFilterParams(item.key, item.code, !item.selected, globalFilter?.items));

    // 세션 스토리지 업데이트
    updateFilterStorage({
      query: currentQuery,
      scrollY: 0,
      sessionKey,
      searchType: currentQuery[searchTypeKey] as string | undefined,
    });

    // 스크롤을 맨 위로 이동
    typeof window !== 'undefined' && window.scrollTo(0, 0);
  };

  return { syncQuickFilterWithQuery };
};
